import React, { useState, useEffect, useRef } from 'react';
import { fetchTournamentStages, fetchTournamentMatches } from '../../api/TournamentsApi';

const TournamentStagesModal = ({ isOpen, onClose, tournamentId, tournamentName }) => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStage, setActiveStage] = useState(null);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const [searchTerm, setSearchTerm] = useState('');
  const [isTeamsDropdownOpen, setIsTeamsDropdownOpen] = useState(false);
  const [matchDetails, setMatchDetails] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Check screen orientation
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // console.log("TournamentStagesModal effect triggered with:", { 
    //   isOpen, tournamentId, tournamentName
    // });
    
    const loadStages = async () => {
      if (!isOpen || !tournamentId) {
        // console.log(!isOpen ? "Modal not open" : "No tournament ID provided", ", skipping fetch");
        return;
      }
      
      try {
        setLoading(true);
        // console.log("About to fetch stages for tournament:", tournamentId);
        
        const stagesData = await fetchTournamentStages(tournamentId);
        // console.log("Tournament stages data received:", stagesData);
        
        if (Array.isArray(stagesData)) {
          // Sort stages by id to ensure they're in the correct order
          const sortedStages = [...stagesData].sort((a, b) => a.id - b.id);
          setStages(sortedStages);
          // Set the first stage as active by default
          if (sortedStages.length > 0 && !activeStage) {
            setActiveStage(sortedStages[0].id);
          }
        } else {
          // console.log("Unexpected data format", stagesData);
          setStages([]);
        }
        
        setError(null);
      } catch (err) {
        console.error("Failed to load tournament stages:", err);
        setError("Failed to load tournament stages. Please try again later.");
        setStages([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadStages();
  }, [isOpen, tournamentId]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsTeamsDropdownOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Add new useEffect to fetch detailed match information
  useEffect(() => {
    const fetchMatches = async () => {
      if (!isOpen || !tournamentId) return;
      
      try {
        const matches = await fetchTournamentMatches(tournamentId);
        
        // Create a lookup object with match ID as key
        const matchLookup = {};
        matches.forEach(match => {
          matchLookup[match.id] = match;
        });
        
        setMatchDetails(matchLookup);
      } catch (err) {
        console.error("Failed to fetch match details:", err);
      }
    };
    
    fetchMatches();
  }, [isOpen, tournamentId]);
  
  if (!isOpen) return null;

  // Format a date string in a readable format
  const formatDate = (dateString) => {
    if (!dateString) return "TBD";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format time from "HH:MM:SS" to "HH:MM AM/PM"
  const formatTime = (timeString) => {
    if (!timeString) return "TBD";
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  // Get color for stage based on position
  const getStageColor = (index, totalStages) => {
    // Color gradient from start to finish
    if (index === 0) return 'bg-emerald-500 border-emerald-600'; // Starting stage
    if (index === totalStages - 1) return 'bg-amber-500 border-amber-600'; // Final stage
    
    // Middle stages get colors based on position
    const position = index / (totalStages - 1);
    if (position < 0.33) return 'bg-teal-500 border-teal-600';
    if (position < 0.66) return 'bg-blue-500 border-blue-600';
    return 'bg-indigo-500 border-indigo-600';
  };
  
  // Filter teams based on search
  const getFilteredTeams = (teams, searchTerm) => {
    if (!searchTerm.trim()) return teams || [];
    
    return (teams || []).filter(team => 
      team.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get match details by ID
  const getMatchInfo = (matchId) => {
    return matchDetails[matchId] || {};
  };
  
  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-gradient-to-b from-gray-900 to-gray-800 text-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-primary-700 to-primary-800 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold flex items-center">
            <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
            {tournamentName ? `${tournamentName} - Tournament Flow` : 'Tournament Flow'}
          </h2>
          <button 
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-auto flex-grow">
          {loading ? (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-400 mb-4"></div>
              <p className="text-yellow-400 animate-pulse">Loading tournament flow...</p>
            </div>
          ) : error ? (
            <div className="bg-red-900/40 border border-red-600 text-red-200 px-4 py-3 rounded-lg">
              <p className="font-bold">Error</p>
              <p>{error}</p>
            </div>
          ) : stages.length > 0 ? (
            <div className="flex flex-col">
              {/* TOURNAMENT FLOW DIAGRAM */}
              <div className={`overflow-x-auto pb-6 ${!isLandscape ? 'pt-12' : 'pt-4'}`}>
                {/* Warning for mobile in portrait */}
                {!isLandscape && (
                  <div className="fixed top-[72px] left-0 right-0 bg-yellow-600 text-white text-center py-2 text-sm z-20">
                    For best view, rotate your device to landscape mode
                  </div>
                )}
                
                {/* Tournament Flow */}
                <div className="min-w-max px-4">
                  <div className="relative flex items-center justify-center">
                    {/* Horizontal line connecting all stages */}
                    <div className="absolute h-1 bg-gray-600 left-0 right-0 top-1/2 transform -translate-y-1/2"></div>
                    
                    {/* Stage boxes with connecting flow */}
                    <div className="flex space-x-12 md:space-x-16 lg:space-x-24 relative">
                      {stages.map((stage, index) => {
                        const isActive = activeStage === stage.id;
                        const stageColor = getStageColor(index, stages.length);
                        
                        return (
                          <div key={stage.id} className="flex flex-col items-center">
                            {/* Stage box */}
                            <div 
                              onClick={() => setActiveStage(stage.id)}
                              className={`
                                w-64 rounded-lg p-4 border-2 ${stageColor} shadow-lg
                                ${isActive ? 'ring-4 ring-white scale-105' : 'opacity-85 hover:opacity-100'} 
                                cursor-pointer transition-all duration-300 transform hover:scale-105 z-10
                              `}
                            >
                              <div className="text-lg font-bold mb-2 text-center text-white">
                                {stage.stage_name}
                              </div>
                              <div className="text-sm space-y-1 text-white">
                                <div className="flex justify-between">
                                  <span>Date:</span>
                                  <span className="font-medium">{formatDate(stage.start_date)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Teams:</span>
                                  <span className="font-medium">{stage.max_teams_to_play}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Matches:</span>
                                  <span className="font-medium">{stage.total_stage_matches}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Teams per Match:</span>
                                  <span className="font-medium">{stage.teams_per_match}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Qualify per Match:</span>
                                  <span className="font-medium">{stage.teams_to_qualify_per_match}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Stage number */}
                            <div className="mt-4 bg-gray-800 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg border-2 border-gray-700">
                              {index + 1}
                            </div>
                            
                            {/* Arrow to next stage */}
                            {index < stages.length - 1 && (
                              <div className="absolute right-[-40px] top-1/2 transform -translate-y-1/2 z-20">
                                <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* STAGE DETAILS SECTION */}
              {activeStage && (
                <div className="mt-8 pt-6 border-t border-gray-700">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {stages.find(s => s.id === activeStage)?.stage_name} Details
                    </h3>
                    
                    {/* Qualified Teams Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                      <button
                        onClick={() => setIsTeamsDropdownOpen(!isTeamsDropdownOpen)}
                        className="flex items-center bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        Qualified Teams
                        <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isTeamsDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-64 bg-gray-800 rounded-md shadow-lg z-50 border border-gray-700 overflow-hidden">
                          <div className="p-2 border-b border-gray-700">
                            <input
                              type="text"
                              placeholder="Search teams..."
                              className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </div>
                          
                          <div className="max-h-60 overflow-y-auto p-1">
                            {(() => {
                              const currentStage = stages.find(s => s.id === activeStage);
                              const qualifiedTeams = currentStage?.qualified_teams || [];
                              const filteredTeams = getFilteredTeams(qualifiedTeams, searchTerm);
                              
                              if (filteredTeams.length === 0) {
                                return (
                                  <div className="px-4 py-3 text-sm text-gray-400 text-center">
                                    {qualifiedTeams.length === 0 
                                      ? "No teams have qualified yet"
                                      : "No teams match your search"}
                                  </div>
                                );
                              }
                              
                              return filteredTeams.map((team, index) => (
                                <div
                                  key={team.id || index}
                                  className="px-4 py-2 hover:bg-gray-700 text-white text-sm cursor-pointer transition-colors rounded-sm flex items-center"
                                  onClick={() => {
                                    // Could add team details view here
                                    setIsTeamsDropdownOpen(false);
                                  }}
                                >
                                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                                    <span className="text-xs font-bold">{index + 1}</span>
                                  </div>
                                  <div className="truncate">{team.name || `Team ${team.id || index + 1}`}</div>
                                </div>
                              ));
                            })()}
                          </div>
                          
                          <div className="p-2 bg-gray-750 border-t border-gray-700 text-xs text-gray-400">
                            Total: {stages.find(s => s.id === activeStage)?.qualified_teams?.length || 0} qualified teams
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Match schedule grid */}
                  {stages.find(s => s.id === activeStage)?.match_dates?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {stages.find(s => s.id === activeStage)?.match_dates.map((match, index) => {
                        const detailedMatchInfo = getMatchInfo(match.match_id);
                        
                        return (
                          <div key={match.match_id || index} className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                            <div className="bg-gray-700 px-4 py-3 flex justify-between items-center">
                              <h4 className="font-bold text-white">Match #{index + 1}</h4>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                match.match_status === 'completed' ? 'bg-green-900 text-green-200' :
                                match.match_status === 'ongoing' ? 'bg-blue-900 text-blue-200 animate-pulse' :
                                'bg-yellow-900 text-yellow-200'
                              }`}>
                                {match.match_status === 'completed' ? 'Completed' :
                                 match.match_status === 'ongoing' ? 'In Progress' :
                                 'Upcoming'}
                              </span>
                            </div>
                            
                            <div className="p-4">
                              <div className="space-y-3 text-gray-300">
                                <div className="flex items-center">
                                  <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <span>{formatDate(match.match_date)}</span>
                                </div>
                                
                                <div className="flex items-center">
                                  <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span>{formatTime(match.match_time)}</span>
                                </div>
                                
                                <div className="flex items-center">
                                  <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                  </svg>
                                  <div>
                                    <span className="text-white">{stages.find(s => s.id === activeStage)?.teams_per_match} Teams</span>
                                    <span className="text-xs text-blue-400 ml-2">
                                      (Top {stages.find(s => s.id === activeStage)?.teams_to_qualify_per_match} Qualify)
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Match ID and Password */}
                              <div className="mt-3 p-3 bg-gray-900 rounded-md border border-gray-700">
                                <h5 className="text-xs uppercase text-gray-400 mb-2 font-semibold">Match Access Info</h5>
                                
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div>
                                    <span className="block text-xs text-gray-500">Match ID</span>
                                    {detailedMatchInfo.match_id ? (
                                      <div className="flex items-center space-x-1">
                                        <span className="font-mono bg-gray-800 px-1.5 py-0.5 rounded text-green-400">
                                          {detailedMatchInfo.match_id}
                                        </span>
                                        <button 
                                          onClick={() => navigator.clipboard.writeText(detailedMatchInfo.match_id)}
                                          className="text-gray-400 hover:text-white"
                                          title="Copy ID"
                                        >
                                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                          </svg>
                                        </button>
                                      </div>
                                    ) : (
                                      <span className="text-gray-600 italic text-xs">Not available yet</span>
                                    )}
                                  </div>
                                  
                                  <div>
                                    <span className="block text-xs text-gray-500">Password</span>
                                    {detailedMatchInfo.match_password ? (
                                      <div className="flex items-center space-x-1">
                                        <span className="font-mono bg-gray-800 px-1.5 py-0.5 rounded text-green-400">
                                          {detailedMatchInfo.match_password}
                                        </span>
                                        <button 
                                          onClick={() => navigator.clipboard.writeText(detailedMatchInfo.match_password)}
                                          className="text-gray-400 hover:text-white"
                                          title="Copy Password"
                                        >
                                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                          </svg>
                                        </button>
                                      </div>
                                    ) : (
                                      <span className="text-gray-600 italic text-xs">Not available yet</span>
                                    )}
                                  </div>
                                </div>
                                
                                {!detailedMatchInfo.match_id && !detailedMatchInfo.match_password && (
                                  <div className="mt-2 text-xs text-amber-500 italic text-center">
                                    ID & password typically available 24 hours before the match
                                  </div>
                                )}
                              </div>
                              
                              {/* Livestream Link */}
                              {detailedMatchInfo.match_livestream_link && (
                                <div className="mt-2">
                                  <a 
                                    href={detailedMatchInfo.match_livestream_link} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center bg-red-700 hover:bg-red-600 text-white px-3 py-2 rounded-md w-full justify-center transition-colors"
                                  >
                                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                                    </svg>
                                    Watch Livestream
                                  </a>
                                </div>
                              )}
                              
                              {/* Match Link Button */}
                              {detailedMatchInfo.match_link ? (
                                <a 
                                  href={detailedMatchInfo.match_link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="w-full mt-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition-colors flex items-center justify-center"
                                >
                                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.102-1.101" />
                                  </svg>
                                  Join Match
                                </a>
                              ) : (
                                <button 
                                  className="w-full mt-4 py-2 bg-gray-600 text-gray-300 rounded font-medium cursor-default"
                                  disabled
                                >
                                  Match Link Not Available Yet
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10 bg-gray-800/30 rounded-lg border border-dashed border-gray-600">
                      <svg className="w-12 h-12 mx-auto text-gray-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-400">No matches scheduled yet for this stage</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Tournament Flow Legend */}
              <div className="mt-6 bg-gray-800 rounded-lg p-3 border border-gray-700">
                <div className="flex flex-wrap gap-4 text-xs text-gray-300">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full mr-1"></div>
                    <span>Qualifier</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-1"></div>
                    <span>Early Rounds</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full mr-1"></div>
                    <span>Late Rounds</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-amber-500 rounded-full mr-1"></div>
                    <span>Finals</span>
                  </div>
                  <div className="ml-auto flex items-center text-gray-400">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                      <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                    </svg>
                    <span>Flow Direction</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-800/30 rounded-lg border border-gray-700">
              <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="text-lg font-medium text-gray-300">No Stages Available</h3>
              <p className="text-gray-400 mt-2">Tournament stages have not been created yet.</p>
            </div>
          )}
        </div>

        <div className="bg-gray-900 px-6 py-4 border-t border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TournamentStagesModal;
